/**
 * HTTP 客户端配置
 * 基于 ky 库的 HTTP 客户端，包含请求/响应拦截器
 */

import ky from 'ky';

/**
 * 默认配置
 */
const defaultConfig = {
  // 基础 URL（开发环境）
  prefixUrl: import.meta.env.PROD ? '' : 'http://localhost:3000/api',
  
  // 超时时间（毫秒）
  timeout: 10000,
  
  // 重试配置
  retry: {
    limit: 2,
    methods: ['get'],
    statusCodes: [408, 413, 429, 500, 502, 503, 504]
  },
  
  // 默认头部
  headers: {
    'Content-Type': 'application/json'
  }
};

/**
 * 创建 HTTP 客户端实例
 */
const client = ky.create({
  ...defaultConfig,
  
  hooks: {
    // 请求前拦截器
    beforeRequest: [
      /**
       * 添加认证头部
       * @param {Request} request - 请求对象
       */
      (request) => {
        // 从本地存储获取 token
        const token = localStorage.getItem('auth_token');
        if (token) {
          request.headers.set('Authorization', `Bearer ${token}`);
        }
      },
      
      /**
       * 添加请求 ID 用于追踪
       * @param {Request} request - 请求对象
       */
      (request) => {
        const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        request.headers.set('X-Request-ID', requestId);
      }
    ],
    
    // 响应后拦截器
    afterResponse: [
      /**
       * 处理响应数据
       * @param {Request} request - 请求对象
       * @param {Object} options - 请求选项
       * @param {Response} response - 响应对象
       * @returns {Response} 处理后的响应
       */
      async (request, options, response) => {
        // 记录响应日志（开发环境）
        if (import.meta.env.DEV) {
          console.log(`[HTTP] ${request.method} ${request.url} - ${response.status}`);
        }
        
        return response;
      }
    ],
    
    // 错误处理拦截器
    beforeError: [
      /**
       * 统一错误处理
       * @param {Error} error - 错误对象
       * @returns {Error} 处理后的错误
       */
      (error) => {
        const { response } = error;
        
        // 401 未授权 - 清除本地 token
        if (response && response.status === 401) {
          localStorage.removeItem('auth_token');
          // 可以在这里触发登录页面跳转
        }
        
        // 记录错误日志
        console.error(`[HTTP Error] ${error.message}`, {
          url: error.request?.url,
          status: response?.status,
          statusText: response?.statusText
        });
        
        return error;
      }
    ]
  }
});

/**
 * 用户相关 API
 */
export const userApi = {
  /**
   * 用户登录
   * @param {Object} credentials - 登录凭据
   * @param {string} credentials.username - 用户名
   * @param {string} credentials.password - 密码
   * @returns {Promise<Object>} 登录结果
   */
  login: async (credentials) => {
    // TODO: 实现用户登录逻辑
    return client.post('auth/login', { json: credentials }).json();
  },

  /**
   * 获取用户信息
   * @returns {Promise<Object>} 用户信息
   */
  getProfile: async () => {
    // TODO: 实现用户信息获取逻辑
    return client.get('auth/profile').json();
  },

  /**
   * 用户登出
   * @returns {Promise<Object>} 登出结果
   */
  logout: async () => {
    // TODO: 实现用户登出逻辑
    return client.post('auth/logout').json();
  }
};

/**
 * 历史上的今天相关 API
 */
export const historyApi = {
  /**
   * 获取历史上的今天数据
   * @param {string} date - 日期格式 MM/DD，如 "8/8"
   * @returns {Promise<Object>} 历史事件数据
   */
  getTodayInHistory: async (date) => {
    // 将日期格式从 "MM/DD" 转换为 "月/日"
    const [month, day] = date.split('/');
    const formattedDate = `${month}/${day}`;
    
    // 使用正确的日期格式构建URL
    const url = `https://history.muffinlabs.com/date/${formattedDate}`;
    
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    } catch (error) {
      console.error('获取历史数据失败:', error);
      // 返回模拟数据以防API调用失败
      return {
        date: `${month}月${day}日`,
        url: `https://wikipedia.org/wiki/${month}_${day}`,
        data: {
          Events: [
            {
              year: "2023",
              text: "这是一个示例历史事件，API调用失败时显示。",
              html: "2023 - 这是一个示例历史事件，API调用失败时显示。",
              links: []
            }
          ]
        }
      };
    }
  }
};

/**
 * 天气相关 API
 */
export const weatherApi = {
  /**
   * 获取当前天气数据
   * @param {Object} params - 查询参数
   * @param {number} params.latitude - 纬度
   * @param {number} params.longitude - 经度
   * @param {string} params.timezone - 时区，默认 'Asia/Shanghai'
   * @returns {Promise<Object>} 天气数据
   */
  getCurrentWeather: async (params = {}) => {
    const {
      latitude = 39.9042, // 北京纬度
      longitude = 116.4074, // 北京经度
      timezone = 'Asia/Shanghai'
    } = params;
    
    const searchParams = new URLSearchParams({
      latitude: latitude.toString(),
      longitude: longitude.toString(),
      timezone,
      current: [
        'temperature_2m',
        'relative_humidity_2m',
        'apparent_temperature',
        'weather_code',
        'wind_speed_10m',
        'wind_direction_10m'
      ].join(','),
      hourly: [
        'temperature_2m',
        'weather_code'
      ].join(','),
      daily: [
        'temperature_2m_max',
        'temperature_2m_min',
        'weather_code'
      ].join(','),
      forecast_days: '3'
    });
    
    const url = `https://api.open-meteo.com/v1/forecast?${searchParams}`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  },

  /**
   * 获取地理位置信息
   * @param {string} city - 城市名称
   * @returns {Promise<Object>} 地理位置数据
   */
  getGeoLocation: async (city) => {
    const searchParams = new URLSearchParams({
      name: city,
      count: '1',
      language: 'zh',
      format: 'json'
    });
    
    const url = `https://geocoding-api.open-meteo.com/v1/search?${searchParams}`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }
};

/**
 * 联系人API
 */
export const contactApi = {
  /**
   * 发送联系表单
   * @param {Object} data - 联系表单数据
   * @returns {Promise} 响应结果
   */
  sendMessage: async (data) => {
    return client.post('contact', { json: data }).json();
  },

  /**
   * 获取联系信息
   * @returns {Promise} 联系信息
   */
  getContactInfo: async () => {
    return client.get('contact/info').json();
  }
};

/**
 * 作品集API
 */
export const portfolioApi = {
  /**
   * 获取作品列表
   * @param {Object} params - 查询参数
   * @returns {Promise} 作品列表
   */
  getProjects: async (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return client.get(`portfolio?${searchParams}`).json();
  },

  /**
   * 获取作品详情
   * @param {string} id - 作品ID
   * @returns {Promise} 作品详情
   */
  getProject: async (id) => {
    return client.get(`portfolio/${id}`).json();
  },

  /**
   * 获取作品分类
   * @returns {Promise} 分类列表
   */
  getCategories: async () => {
    return client.get('portfolio/categories').json();
  }
};

/**
 * 分析API
 */
export const analyticsApi = {
  /**
   * 记录页面访问
   * @param {Object} data - 访问数据
   * @returns {Promise} 响应结果
   */
  trackPageView: async (data) => {
    return client.post('analytics/pageview', { json: data }).json();
  },

  /**
   * 记录事件
   * @param {Object} data - 事件数据
   * @returns {Promise} 响应结果
   */
  trackEvent: async (data) => {
    return client.post('analytics/event', { json: data }).json();
  },

  /**
   * 获取统计数据
   * @param {Object} params - 查询参数
   * @returns {Promise} 统计数据
   */
  getStats: async (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return client.get(`analytics/stats?${searchParams}`).json();
  }
};

export default client;